import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { format } from 'date-fns';

export function UserGrowthChart({ data, title = "User Growth (Last 30 Days)", compact = false }) {
  const formatTooltip = (value, name, props) => {
    if (name === 'users') {
      return [value, 'Total Users'];
    }
    return [value, name];
  };

  const formatXAxis = (tickItem) => {
    return format(new Date(tickItem), 'MMM dd');
  };

  const height = compact ? 200 : 300;

  return (
    <div className={compact ? '' : 'bg-white rounded-lg border p-4'}>
      {!compact && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatXAxis}
            stroke="#666"
            fontSize={compact ? 10 : 12}
          />
          <YAxis stroke="#666" fontSize={compact ? 10 : 12} />
          <Tooltip 
            formatter={formatTooltip}
            labelFormatter={(label) => format(new Date(label), 'MMM dd, yyyy')}
            contentStyle={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="users" 
            stroke="#3b82f6" 
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: compact ? 3 : 4 }}
            activeDot={{ r: compact ? 4 : 6, fill: '#1d4ed8' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SubscriptionPieChart({ data, title = "User Subscription Breakdown", compact = false }) {
  const COLORS = ['#ef4444', '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6'];

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={compact ? 10 : 12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const height = compact ? 180 : 300;
  const outerRadius = compact ? 60 : 80;

  return (
    <div className={compact ? '' : 'bg-white rounded-lg border p-4'}>
      {!compact && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={outerRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [value, name]}
            contentStyle={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px'
            }}
          />
          {!compact && <Legend />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TierComparisonChart({ freeUserStats, proUserStats, title = "Free vs Pro User Activity" }) {
  const data = [
    {
      name: 'Project Groups',
      Free: freeUserStats.projectGroups,
      Pro: proUserStats.projectGroups,
    },
    {
      name: 'Projects',
      Free: freeUserStats.projects,
      Pro: proUserStats.projects,
    },
    {
      name: 'Tasks',
      Free: freeUserStats.tasks,
      Pro: proUserStats.tasks,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-800">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" stroke="#666" fontSize={12} />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '6px'
              }}
            />
            <Legend />
            <Bar dataKey="Free" fill="#ef4444" radius={[2, 2, 0, 0]} />
            <Bar dataKey="Pro" fill="#22c55e" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
